import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash, Check, X, Search, CheckCircle, XCircle, Pencil, Save, BarChart2 } from "lucide-react";
import { useQuizStore, QuizQuestion, QuizOption } from "@/data/quizStore";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { allPartiesIncludingNonParliamentary as PARTIES } from "@/data/parties";

export default function AdminQuizPage() {
  const { questions, addQuestion, updateQuestion, updateQuestionStatus, deleteQuestion, itemsPerSession, updateItemsPerSession, consolidatedParties, analytics, resetAnalytics } = useQuizStore();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState<'questions' | 'moderation' | 'config' | 'stats'>('questions');
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<QuizQuestion> | null>(null);

  const [isCreating, setIsCreating] = useState(false);
  const [localItems, setLocalItems] = useState(itemsPerSession);

  useEffect(() => {
    setLocalItems(itemsPerSession);
  }, [itemsPerSession]);

  const filteredQuestions = questions.filter(q => {
    const matchSearch = q.question.fr.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === "all" || q.status === filterStatus;
    const matchTab = activeTab === 'questions' ? q.status === 'valide' : q.status === 'en_attente';
    return matchSearch && matchStatus && matchTab;
  });

  const getPartyName = (id: string) => {
    const party = PARTIES.find(p => p.acronym === id);
    return party ? party.acronym : id;
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    setEditingId("new");
    setEditForm({
      question: { fr: "", ar: "", zgh: "" },
      options: [
        { label: { fr: "Option 1", ar: "", zgh: "" }, scores: {} },
        { label: { fr: "Option 2", ar: "", zgh: "" }, scores: {} },
        { label: { fr: "Option 3", ar: "", zgh: "" }, scores: {} },
        { label: { fr: "Option 4", ar: "", zgh: "" }, scores: {} }
      ],
      status: "valide",
      submittedBy: "admin"
    });
  };

  const handleStartEdit = (q: QuizQuestion) => {
    setIsCreating(false);
    setEditingId(q.id);
    setEditForm(JSON.parse(JSON.stringify(q))); // deep copy
  };

  const handleSaveEdit = async () => {
    if (!editForm || !editForm.question?.fr) {
      toast({ title: "Erreur", description: "La question en français est obligatoire.", variant: "destructive" });
      return;
    }
    
    if (editingId === "new") {
      await addQuestion(editForm as Omit<QuizQuestion, 'id' | 'createdAt'>);
      toast({ title: "Question ajoutée" });
    } else {
      await updateQuestion(editingId!, editForm);
      toast({ title: "Question modifiée" });
    }
    
    setEditingId(null);
    setEditForm(null);
    setIsCreating(false);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
    setIsCreating(false);
  };

  const togglePartyForOption = (optionIndex: number, partyAcronym: string) => {
    if (!editForm || !editForm.options) return;
    const newOptions = [...editForm.options];
    const option = newOptions[optionIndex];
    if (!option.scores) option.scores = {};
    
    if (option.scores[partyAcronym]) {
      delete option.scores[partyAcronym];
    } else {
      option.scores[partyAcronym] = 1;
    }
    setEditForm({ ...editForm, options: newOptions });
  };

  const updateOptionText = (optionIndex: number, lang: 'fr'|'ar'|'zgh', text: string) => {
    if (!editForm || !editForm.options) return;
    const newOptions = [...editForm.options];
    newOptions[optionIndex].label[lang] = text;
    setEditForm({ ...editForm, options: newOptions });
  };

  const handleStatusChange = async (id: string, status: QuizQuestion['status']) => {
    await updateQuestionStatus(id, status);
    toast({ title: "Statut mis à jour", description: `La question est maintenant ${status}.` });
  };

  const handleDelete = async (id: string) => {
    if (confirm("Êtes-vous sûr de vouloir supprimer cette question ?")) {
      await deleteQuestion(id);
      toast({ title: "Supprimée", description: "La question a été supprimée." });
    }
  };

  const validProps = questions.filter(p => p.status === 'valide');
  const pendingProps = questions.filter(p => p.status === 'en_attente');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">Quiz Politique</h1>
          <p className="text-muted-foreground">Gérez les questions du quiz et validez les propositions des partis.</p>
        </div>
      </div>

      <div className="flex gap-2 border-b">
        <button onClick={() => setActiveTab('questions')} className={`px-4 py-2 font-semibold text-sm border-b-2 ${activeTab === 'questions' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
          Questions Actives ({validProps.length})
        </button>
        <button onClick={() => setActiveTab('moderation')} className={`px-4 py-2 font-semibold text-sm border-b-2 flex items-center gap-2 ${activeTab === 'moderation' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
          Modération des Partis {pendingProps.length > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{pendingProps.length}</span>}
        </button>
        <button onClick={() => setActiveTab('config')} className={`px-4 py-2 font-semibold text-sm border-b-2 flex items-center gap-2 ${activeTab === 'config' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
          Configuration
        </button>
        <button onClick={() => setActiveTab('stats')} className={`px-4 py-2 font-semibold text-sm border-b-2 flex items-center gap-2 ${activeTab === 'stats' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
          <BarChart2 className="w-4 h-4" /> Statistiques
        </button>
      </div>

      {activeTab === 'questions' && (
        <Card className="border shadow-sm">
          <CardHeader className="pb-3 border-b border-border bg-muted/10">
            <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
              <div>
                <h2 className="font-semibold text-lg">Base de questions</h2>
                {consolidatedParties && consolidatedParties.length > 0 && (
                  <p className="text-xs text-emerald-600 font-medium mt-1">
                    ✓ Partis ayant validé leurs choix : {consolidatedParties.join(', ')}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="relative w-full md:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input 
                    placeholder="Rechercher..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 h-9 text-sm"
                  />
                </div>
                <Button onClick={handleStartCreate} className="h-9 gap-2 bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus className="w-4 h-4" /> Nouvelle
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {editingId && editForm ? (
              <div className="p-6 bg-muted/5 space-y-6">
                <div className="flex justify-between items-center border-b pb-4">
                  <h3 className="font-bold text-lg">{isCreating ? "Nouvelle Question" : "Modifier la Question"}</h3>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleCancelEdit}>Annuler</Button>
                    <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700 gap-2"><Save className="w-4 h-4" /> Enregistrer</Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2 md:col-span-1">
                    <label className="text-sm font-semibold">Question (Français) *</label>
                    <Input value={editForm.question?.fr || ""} onChange={e => setEditForm({...editForm, question: {...editForm.question!, fr: e.target.value}})} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold">Question (Arabe)</label>
                    <Input value={editForm.question?.ar || ""} onChange={e => setEditForm({...editForm, question: {...editForm.question!, ar: e.target.value}})} dir="rtl" />
                  </div>
                </div>

                <div className="space-y-6 mt-6">
                  <h4 className="font-bold text-md border-b pb-2">Réponses (Options) et Affectations des Partis</h4>
                  <div className="grid grid-cols-1 gap-6">
                    {editForm.options?.map((opt, idx) => (
                      <div key={idx} className="p-4 border rounded-xl bg-background space-y-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-bold text-blue-600 bg-blue-100 w-6 h-6 rounded-full flex items-center justify-center text-xs">{idx + 1}</span>
                          <span className="font-semibold text-sm">Option {idx + 1}</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Input value={opt.label.fr} onChange={e => updateOptionText(idx, 'fr', e.target.value)} placeholder="Texte (FR)" />
                          <Input value={opt.label.ar} onChange={e => updateOptionText(idx, 'ar', e.target.value)} placeholder="Texte (AR)" dir="rtl" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Partis associés (marqueront un point)</label>
                          <div className="flex flex-wrap gap-2">
                            {PARTIES.map(p => (
                              <button 
                                type="button" 
                                key={p.acronym} 
                                onClick={() => togglePartyForOption(idx, p.acronym)} 
                                className={`px-2 py-1 text-xs rounded-full border transition-colors ${opt.scores && opt.scores[p.acronym] ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/50 text-muted-foreground hover:bg-muted'}`}
                              >
                                {p.acronym}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={handleCancelEdit}>Annuler</Button>
                  <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700 gap-2"><Save className="w-4 h-4" /> Enregistrer</Button>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredQuestions.map((q) => (
                  <div key={q.id} className="p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:bg-muted/30 transition-colors">
                    <div className="flex-1 space-y-2">
                      <h4 className="font-bold text-base">{q.question.fr}</h4>
                      <p className="text-sm text-muted-foreground font-arabic" dir="rtl">{q.question.ar}</p>
                      
                      <div className="flex flex-wrap gap-2 mt-2">
                        {q.options.map((opt, i) => (
                          <div key={i} className="text-xs bg-muted border px-2 py-1 rounded flex flex-col gap-1">
                            <span className="font-semibold text-foreground/80">{i+1}. {opt.label.fr}</span>
                            <div className="flex flex-wrap gap-1">
                              {Object.keys(opt.scores || {}).map(p => (
                                <span key={p} className="text-[9px] bg-primary/10 text-primary px-1 rounded">{p}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-muted-foreground">Soumis par: <strong>{q.submittedBy}</strong></span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleStartEdit(q)} className="text-blue-600 hover:bg-blue-50 h-8 px-2 gap-1">
                        <Pencil className="w-4 h-4" /> Modifier
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(q.id)} className="text-red-600 hover:bg-red-50 h-8 px-2">
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {filteredQuestions.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    Aucune question trouvée.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'moderation' && (
        <Card className="border shadow-sm">
          <CardHeader className="bg-muted/10 border-b">
             <CardTitle className="text-lg">Soumissions des Partis</CardTitle>
             <CardDescription>Modérez les propositions de questions pour le quiz soumises par les partis politiques.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
             {pendingProps.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">Aucune question en attente de validation.</div>
             ) : (
                <div className="divide-y">
                   {pendingProps.map(q => (
                      <div key={q.id} className="p-4 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-background">
                         <div className="flex-1 space-y-2">
                           <div className="flex items-center gap-2 mb-1">
                             <Badge className="bg-amber-100 text-amber-800 border-amber-200">En attente</Badge>
                             <span className="text-xs text-muted-foreground">Soumis par : <strong>{q.submittedBy}</strong></span>
                           </div>
                           <h4 className="font-bold text-base">{q.question.fr}</h4>
                           <p className="text-sm text-muted-foreground font-arabic" dir="rtl">{q.question.ar}</p>
                           
                           <div className="flex flex-wrap gap-2 mt-2">
                             {q.options.map((opt, i) => (
                               <div key={i} className="text-xs bg-muted border px-2 py-1 rounded flex flex-col gap-1">
                                 <span className="font-semibold text-foreground/80">{opt.label.fr}</span>
                                 <div className="flex flex-wrap gap-1">
                                    {Object.keys(opt.scores || {}).map(p => (
                                      <span key={p} className="text-[9px] bg-primary/10 text-primary px-1 rounded">{p}</span>
                                    ))}
                                 </div>
                               </div>
                             ))}
                           </div>
                         </div>
                         <div className="flex gap-2">
                           <Button variant="outline" size="sm" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => handleStatusChange(q.id, 'valide')}>
                              <CheckCircle className="w-4 h-4 mr-1"/> Accepter
                           </Button>
                           <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => handleStatusChange(q.id, 'rejete')}>
                              <XCircle className="w-4 h-4 mr-1"/> Refuser
                           </Button>
                           <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => { setActiveTab('questions'); handleStartEdit(q); }}>
                              <Pencil className="w-4 h-4 mr-1"/> Éditer
                           </Button>
                         </div>
                      </div>
                   ))}
                </div>
             )}
          </CardContent>
        </Card>
      )}

       {activeTab === 'config' && (
        <Card className="border shadow-sm max-w-2xl">
          <CardHeader className="bg-muted/10 border-b">
             <CardTitle className="text-lg">Paramètres du Quiz</CardTitle>
             <CardDescription>Configuration du déroulement du jeu pour les utilisateurs.</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
             <div className="space-y-2">
                <label className="text-sm font-semibold">Nombre de questions par session (Min 5, Max 20)</label>
                <div className="flex gap-2 max-w-sm">
                  <Input type="number" min={5} max={20} value={localItems} onChange={e => setLocalItems(parseInt(e.target.value) || 5)} />
                  <Button onClick={() => {
                    updateItemsPerSession(localItems);
                    toast({ title: "Configuration sauvegardée", description: "Le nombre de questions par session a été mis à jour." });
                  }} className="gap-2 bg-primary">
                    <CheckCircle className="w-4 h-4" /> Valider le choix
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">À chaque fois qu'un utilisateur joue, ce nombre exact de questions sera tiré de la base. Si l'utilisateur rejoue, 2 ou 3 questions resteront les mêmes et les autres seront nouvelles, pour l'effet de surprise.</p>
             </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'stats' && (
        <Card className="border shadow-sm max-w-2xl">
          <CardHeader className="bg-muted/10 border-b">
             <CardTitle className="text-lg">Statistiques du Quiz</CardTitle>
             <CardDescription>Aperçu de la participation des citoyens au module Quiz.</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div className="bg-card rounded-xl border p-4 shadow-sm text-center">
                 <p className="text-sm text-muted-foreground font-semibold">Participations Totales</p>
                 <p className="text-4xl font-black font-display text-primary mt-2">{analytics?.totalPlays || 0}</p>
               </div>
             </div>
             
             <div className="mt-8 border-t pt-6">
                <Button variant="outline" size="sm" onClick={() => {
                  if (confirm("Êtes-vous sûr de vouloir remettre les compteurs à zéro ?")) {
                    resetAnalytics();
                    toast({ title: "Compteurs réinitialisés" });
                  }
                }} className="text-destructive border-destructive hover:bg-destructive/10">Réinitialiser les stats</Button>
             </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
